//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import { EditorState, Modifier } from 'draft-js';
import React, { useEffect, useReducer } from 'react';
import styled from 'styled-components';

import { Alert } from '@components/atoms/alert/Alert';
import { PrimaryAnchor } from '@components/atoms/anchor/Anchor';
import Icon from '@components/atoms/icon/Icon';
import Spinner from '@components/atoms/spinner/Spinner';
import { BodyBoldText } from '@components/atoms/text/Text';
import TextAreaInput from '@components/atoms/text-area-input/TextAreaInput';
import { Modal } from '@components/molecules/modal/Modal';
import { useSmartInputContext } from '@components/molecules/smart-input/SmartInputContext';
import { SmartInputTrackingId } from '@components/molecules/smart-input/smartInputTrackingIds';
import { getSmartInputTrackingId } from '@components/molecules/smart-input/smartInputUtils';
import { isDefined } from '@utils/filterPredicates';

const DEFAULT_ERROR_MESSAGE = 'Unexpected error getting generative ai caption';

interface IGenerativeAiCaptionState {
  caption: string | undefined;
  error: string | undefined;
  isGettingCaption: boolean;
}

type GenerativeAiCaptionAction =
  | { type: 'RequestCaption' }
  | {
      type: 'ReceiveCaptionSucceeded';
      payload: {
        caption: string;
      };
    }
  | {
      type: 'ReceieveCaptionFailed';
      payload: {
        error: string;
      };
    };

const initialState: IGenerativeAiCaptionState = {
  caption: undefined,
  isGettingCaption: false,
  error: undefined,
};

const reducer = (
  state: IGenerativeAiCaptionState,
  action: GenerativeAiCaptionAction,
): IGenerativeAiCaptionState => {
  switch (action.type) {
    case 'RequestCaption': {
      return {
        caption: state.caption,
        isGettingCaption: true,
        error: undefined,
      };
    }
    case 'ReceiveCaptionSucceeded': {
      return {
        caption: action.payload.caption,
        isGettingCaption: false,
        error: undefined,
      };
    }
    case 'ReceieveCaptionFailed': {
      return {
        caption: undefined,
        isGettingCaption: false,
        error: action.payload.error,
      };
    }
  }
};

interface Props {
  closeModal: () => void;
  isOpen: boolean;
}

export const GenerativeAiCaptionModal: React.FunctionComponent<Props> = ({
  closeModal,
  isOpen,
}) => {
  const smartInputContext = useSmartInputContext();

  const [{ caption, error, isGettingCaption }, dispatch] = useReducer(
    reducer,
    initialState,
  );

  useEffect(() => {
    void handleGetGeneratedCaption();
  }, []);

  const isPrimaryModalBtnDisabled =
    isDefined(error) || isGettingCaption || !caption;

  const handleGetGeneratedCaption = async () => {
    dispatch({ type: 'RequestCaption' });

    try {
      const generatedCaption =
        await smartInputContext.generativeAiCaption?.getGeneratedCaption();

      if (generatedCaption) {
        dispatch({
          type: 'ReceiveCaptionSucceeded',
          payload: { caption: generatedCaption.trim() },
        });
      } else {
        throw new Error(DEFAULT_ERROR_MESSAGE);
      }
    } catch (e) {
      const errorMessage =
        e instanceof Error ? e.message : DEFAULT_ERROR_MESSAGE;

      console.error(errorMessage);

      dispatch({
        type: 'ReceieveCaptionFailed',
        payload: { error: errorMessage },
      });
    }
  };

  const handleInsertGeneratedCaptionAndCloseModal = () => {
    // Select current caption
    const currentEditorState = smartInputContext.editor.getEditorState();
    const currentContentState = currentEditorState.getCurrentContent();

    const currentSelectionState = currentEditorState.getSelection().merge({
      anchorKey: currentContentState.getFirstBlock().getKey(),
      anchorOffset: 0,

      focusOffset: currentContentState.getLastBlock().getText().length,
      focusKey: currentContentState.getLastBlock().getKey(),
    });

    // Replaces any currently selected text with the new text
    const textContent = Modifier.replaceText(
      currentContentState,
      currentSelectionState,
      caption ?? '',
    );

    const newEditorState = EditorState.push(
      currentEditorState,
      textContent,
      'insert-fragment',
    );

    smartInputContext.editor.setEditorState(
      EditorState.forceSelection(
        newEditorState,
        textContent.getSelectionAfter(),
      ),
      true,
    );

    closeModal();

    // If the inserted caption has a hashtag it will trigger the recommended hashtags dropdown to open
    // In order to make sure it is closed, we need to push closing it to the next tick
    setTimeout(() => {
      smartInputContext.recommendedHashtags?.setIsDropdownOpen(false);
    }, 0);
  };

  return (
    <Modal
      closeModal={closeModal}
      contents={
        <Wrapper>
          {isGettingCaption && (
            <LoadingWrapper>
              <Spinner />
              <BodyBoldText>Generating...</BodyBoldText>
            </LoadingWrapper>
          )}
          {!isGettingCaption && !isDefined(error) && (
            <TextAreaInput
              label='AI auto-generated this caption. The caption is based on several
                factors including tagged products, captions from your recent
                posts, and image content.'
              readOnly
              rows={12}
              value={caption ?? ''}
            />
          )}
          {!isGettingCaption && isDefined(error) && (
            <PaddedAlert type='error'>
              There was an error generating a caption. Please try again or
              contact Bazaarvoice Support.
            </PaddedAlert>
          )}
          <TermsWrapper>
            By using this caption, you agree that Bazaarvoice may input relevant
            client data to OpenAI and that you will abide by OpenAI's&nbsp;
            <PrimaryAnchor
              href='https://openai.com/policies/terms-of-use'
              target='_blank'
            >
              terms of use <Icon name='linkout' />
            </PrimaryAnchor>
            .
          </TermsWrapper>
        </Wrapper>
      }
      isModalOpen={isOpen}
      primaryButtonInfo={{
        isDisabled: isPrimaryModalBtnDisabled,
        onClick: handleInsertGeneratedCaptionAndCloseModal,
        buttonContent: 'Use Caption',
        trackingId: getSmartInputTrackingId(
          smartInputContext.trackingIdPrefix,
          SmartInputTrackingId.GenerativeAiCaptionModalAddCaptionButton,
        ),
      }}
      secondaryButtonInfo={{
        buttonContent: 'Cancel',
        onClick: () => closeModal(),
        trackingId: getSmartInputTrackingId(
          smartInputContext.trackingIdPrefix,
          SmartInputTrackingId.GenerativeAiCaptionModalCancelButton,
        ),
      }}
      title={'Auto-Generated Caption'}
      trackingId={getSmartInputTrackingId(
        smartInputContext.trackingIdPrefix,
        SmartInputTrackingId.GenerativeAiCaptionModal,
      )}
      width='wide'
    />
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 300px;
  max-height: 360px;
`;

const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: auto;
`;

const PaddedAlert = styled(Alert)(
  ({ theme }) => `
  margin-top: ${theme.spacings[2]};
`,
);

const TermsWrapper = styled.div(
  ({ theme }) => `
  margin-top: ${theme.spacings[2]}
`,
);
